import toast from 'react-hot-toast';
import http from 'services/http';
import {
  Eligibility,
  Filters,
  Plan,
  PlanView,
  ProgramCard,
  ProgramList,
  ProgramSession,
  SearchResult,
  Suggestions,
  TaskView,
  TopicGroup,
  TrackedList,
  WeeksView,
} from './types';

/** Error body for the free-plan limit (403 { error: 'plan_required' }). */
export const isPlanRequired = (e: any) => e?.response?.status === 403 && e?.response?.data?.error === 'plan_required';
export const errorText = (e: any, fallback = 'Something went wrong. Please try again.') => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(' ') : typeof m === 'string' && m ? m : fallback;
};

/**
 * services/http already shows a message for server errors (except GET 404), so
 * screens only add one when the request never reached the server.
 */
export function showError(e: any, fallback = 'Something went wrong. Please try again.') {
  if (!e?.response) toast.error(fallback);
}

/** services/http resolves with the response body (its interceptor unwraps it). */
const body = <T,>(p: Promise<unknown>) => p as Promise<T>;

/** Turns the Discover filters into search query params. Empty values are left out. */
export function searchParams(f: Filters, elig: Eligibility | null, page: number, limit = 24): Record<string, string> {
  const p: Record<string, string> = { page: String(page), limit: String(limit), sort: f.sort };
  const put = (k: string, v: unknown) => {
    if (v === undefined || v === null || v === '' || v === false) return;
    if (Array.isArray(v)) {
      if (v.length) p[k] = v.join(',');
      return;
    }
    p[k] = String(v);
  };
  put('q', f.q.trim());
  put('when', f.when);
  put('types', f.types);
  put('competitiveness', f.competitiveness);
  put('topics', f.topics);
  put('pays_only', f.pays_only);
  put('free_only', f.free_only);
  put('aid_only', f.aid_only);
  if (f.cost_max) {
    p.cost_max = f.cost_max;
    p.include_unpriced = String(f.include_unpriced);
  }
  put('delivery', f.delivery);
  put('housing', f.housing);
  put('grades', f.grades);
  if (/^\d{5}$/.test(f.near_zip)) {
    p.near_zip = f.near_zip;
    p.near_miles = String(f.near_miles);
    put('commute_only', f.commute_only);
  }
  put('open_only', f.open_only);
  put('group_family', f.group_family);
  if (f.open_to_me && elig) {
    put('elig_age', elig.elig_age);
    put('elig_state', elig.elig_state);
    put('elig_citizenship', elig.elig_citizenship);
    put('elig_gender', elig.elig_gender);
  }
  return p;
}

export const epApi = {
  search: (params: Record<string, string>) => body<SearchResult>(http.get('api/programs/search', { params })),
  topics: () => body<TopicGroup[]>(http.get('api/programs/topics')),
  myEligibility: () => body<Eligibility>(http.get('api/programs/my-eligibility')),
  /** "Suggested for you" (Gold). Not Gold: available false, so the screen shows the teaser. */
  suggested: (sid: number) => body<Suggestions>(http.get(`api/programs/suggested/${sid}`)),
  hideSuggestion: (sid: number, pid: number) => body<{ ok: boolean }>(http.post(`api/programs/suggested/${sid}/${pid}/hide`)),
  program: (id: number) => body<ProgramCard>(http.get(`api/programs/${id}`)),
  programTopics: (id: number) => body<{ slug: string; name: string }[]>(http.get(`api/programs/${id}/topics`)),
  sessions: (id: number) => body<ProgramSession[]>(http.get(`api/programs/${id}/sessions`)),
  recordView: (id: number) => http.post(`api/programs/${id}/view`).catch(() => undefined),
  /** After a program: add it to Activities & Honors (draft, once). Returns where to open it. */
  addToActivities: (sid: number, pid: number) => body<{ index: number; added: boolean; link: string }>(http.post(`api/tasks/${sid}/programs/${pid}/add-to-activities`)),
  /** "Notify me when applications open" (students only). */
  follows: () => body<{ program_id: number; created_at: string; notified_at: string | null }[]>(http.get('api/program-follows')),
  follow: (id: number) => body<{ program_id: number; following: boolean; message?: string }>(http.post(`api/program-follows/${id}`)),
  unfollow: (id: number) => body<{ program_id: number; following: boolean }>(http.delete(`api/program-follows/${id}`)),

  savedIds: () =>
    body<{ data: { id: number }[] }>(http.get('api/wp-geodir/saved-programs')).then((r) => new Set<number>((r?.data ?? []).map((p) => Number(p.id)))),
  saved: () => body<{ data: ProgramCard[] }>(http.get('api/wp-geodir/saved-programs')).then((r) => r?.data ?? []),
  save: (programId: number) => http.post('api/wp-geodir/saved-programs', { program_id: programId }),
  unsave: (programId: number) => http.delete(`api/wp-geodir/saved-programs/${programId}`),

  plan: (sid: number, pid: number) => body<PlanView>(http.get(`api/tasks/${sid}/programs/${pid}/plan`)),
  setPlan: (sid: number, pid: number, data: { plan: Plan; session_id?: number | null; deadline_choice?: string | null }) =>
    body<PlanView>(http.put(`api/tasks/${sid}/programs/${pid}/plan`, data)),
  setDetails: (sid: number, pid: number, data: { portal_url?: string | null; portal_username?: string | null }) =>
    body<PlanView>(http.put(`api/tasks/${sid}/programs/${pid}/details`, data)),
  setDecision: (sid: number, pid: number, decision: string) => body<PlanView>(http.put(`api/tasks/${sid}/programs/${pid}/decision`, { decision })),
  tracked: (sid: number) => body<TrackedList>(http.get(`api/tasks/${sid}/programs`)),
  weeks: (sid: number) => body<WeeksView>(http.get(`api/tasks/${sid}/weeks`, { params: { weeks: 6 } })),
  calendarLink: (sid: number) => body<{ path: string }>(http.get(`api/tasks/${sid}/calendar/link`)),

  updateTask: (sid: number, taskId: number, data: Partial<TaskView>) => body<TaskView>(http.patch(`api/tasks/${sid}/${taskId}`, data)),
  addTask: (sid: number, data: { title: string; program_id?: number; student_target_date?: string; source?: string }) =>
    body<TaskView>(http.post(`api/tasks/${sid}`, data)),

  lists: (sid: number) => body<{ data: ProgramList[] }>(http.get(`api/program-lists/student/${sid}`)).then((r) => r?.data ?? []),
  respond: (itemId: number, response: 'added' | 'not_for_me') => body<unknown>(http.post(`api/program-lists/items/${itemId}/respond`, { response })),

  // Counselor and parent tab
  studentEligibility: (sid: number) => body<Eligibility>(http.get(`api/programs/eligibility/${sid}`)),
  createList: (sid: number, name: string) => body<{ id: number }>(http.post('api/program-lists', { student_id: sid, name })),
  addToList: (listId: number, programId: number, note?: string) =>
    body<unknown>(http.post(`api/program-lists/${listId}/items`, { program_id: programId, note: note || null })),
  nudge: (sid: number, programId: number) => body<unknown>(http.post(`api/program-lists/student/${sid}/nudge`, { program_id: programId })),
};
